import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import analyticsService from "../AnalyticsService";
import { useSelector } from "react-redux";

const pageNameMap = {
  "/register": "Registration Page",
  "/user/register": "Registration Page",
  "/email-verified": "Post Registration Notification Page",
  "/user/email-verified": "Post Registration Notification Page",
  "/activate": "Activate Page",
  "/login": "Login Page",
  "/user/login": "Login Page",
  "/forgot": "Forgot Password Page",
  "/user/forgot": "Forgot Password Page",
  "/user/bankperformancemetrics": "Bank Performance Metrics Page",
  "/ewiChatBot/ewi": "Insights Page",
  "/ewiChatBot/aria": "ARIA Page",
  "/drilldown/graph": "Cluster Page",
  "/drilldown/list/": "View Details Page",
  "/bank/health-analysis/history": "History Page",
  "/cohort-management": "Cohort Management",
  "/bank/campaign-management/email-campaigns": "Email Campaign Page",
  "/bank/campaign-management/create-campaign": "Create Campaign Page",
  "/bank/campaign-management/email-templates": "Email Templates Page",
  "/bank/campaign-management/create-template": "Create Template Page",
  "/bank/campaign-management/edit-campaign": "Edit Campaign Page",
  "/bank/campaign-management/update-email-template": "Template Editor Page",
  "/bank/campaign-management/review-email": "Review Campaign Page",
  "/bank/campaign-management/consumers-list": "Consumers List Page",
  "/bank/user-management/user-management-list": "User Management Page",
  "/pages/faq": "Support Page",
};

const resolvePageName = (currentPath) => {
  if (!currentPath) {
    return null;
  }

  const normalizedPath = currentPath.split("?")[0];

  if (normalizedPath.includes("/bank/health-analysis/report/")) {
    const reportSubPath = normalizedPath.replace(
      "/bank/health-analysis/report",
      "",
    );

    if (reportSubPath.includes("/financial-health-score")) {
      return "Analyst Dashboard Page";
    }

    if (reportSubPath.includes("/drilldown/graph/")) {
      return "Cluster Page";
    }

    if (reportSubPath.includes("/drilldown/list/")) {
      return "View Details Page";
    }

    if (reportSubPath.includes("/ewiChatBot/aria")) {
      return "ARIA Page";
    }

    return "Dashboard Page";
  }


  const match = Object.entries(pageNameMap)
    .filter(([route]) => {
      const normalizedRoute = route.endsWith("/") ? route.slice(0, -1) : route;
      return (
        normalizedPath === normalizedRoute ||
        normalizedPath.startsWith(`${normalizedRoute}/`)
      );
    })
    .sort((left, right) => right[0].length - left[0].length)[0];

  return match?.[1] || null;
};

export const usePageTracker = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const lastTrackedContextKey = useRef(null);
  const currentDashboard = useSelector(
    (state) => state?.dashboardReducer?.currentDashboard,
  );
  const selectedCard = useSelector(
    (state) => state?.dashboardReducer?.selectedCard,
  );

  const currentPath = `${location?.pathname}${location?.search || ""}`;
  const currentDashboardTitle =
    currentDashboard?.CONFIG?.pageHeader?.pageTitle || null;
  const selectedCardName =
    currentDashboard?.KEY === 2
      ? selectedCard?.commercial_title
      : selectedCard?.retail_title;
  const trackingContextKey = `${currentPath}|${navigationType}|${currentDashboardTitle ?? ""}|${selectedCardName ?? ""}|${location?.pathname ?? ""}`;

  useEffect(() => {
    if (lastTrackedContextKey.current === trackingContextKey) {
      return undefined;
    }

    let timeoutId = null;
    let loadHandler = null;

    const runTracking = () => {
      lastTrackedContextKey.current = trackingContextKey;

      const pageName = resolvePageName(currentPath);

      analyticsService.trackPageView({
        page_name: pageName || document?.title,
        page_path: currentPath,
        navigation_type: navigationType,
        dashboard: currentDashboardTitle,
        navigated_from_card: selectedCardName || null,
      });
    };

    const scheduleTracking = () => {
      if (document.readyState === "complete") {
        timeoutId = window.setTimeout(runTracking, 150);
        return;
      }

      loadHandler = () => {
        timeoutId = window.setTimeout(runTracking, 150);
      };

      window.addEventListener("load", loadHandler, { once: true });
    };

    scheduleTracking();

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      if (loadHandler) {
        window.removeEventListener("load", loadHandler);
      }
    };
  }, [
    trackingContextKey,
    currentPath,
    navigationType,
    currentDashboardTitle,
    selectedCardName,
  ]);
};
